import { RefEntity, TableRow } from './types';
import type { AdditionalVariable } from '../CreateProduct/createProduct.interface';

export interface CategoryOption extends RefEntity {
  categoryOneId?: unknown;
  categoryTwoId?: unknown;
  categoryThreeId?: unknown;
}

export interface ParentMatch {
  relations: string[];
  id: unknown;
}

export interface CascadeSources {
  subBrandsByBrand: Record<string, RefEntity[]>;
  licensesByLicensor: Record<string, RefEntity[]>;
  likenessByLicense: Record<string, RefEntity[]>;
  productTypeProcesses: RefEntity[];
  cat1Source: RefEntity[];
  cat2Source: RefEntity[];
  /**
   * Tenant setting: when true, Category 1 is scoped to the row's product type and Category 2 to the
   * row's Category 1. When false the full masters apply, exactly as CreateProduct behaves.
   */
  catBasedOnProductType: boolean;
  cat3Source: RefEntity[];
  cat4Source: RefEntity[];
  sizeRangeMaster: unknown;
  supplierFactories: unknown;
  additionalVariables: ScopedAdditionalVariable[];
  seasonSource: RefEntity[];
}

export const toOptions = (list: unknown): RefEntity[] => (Array.isArray(list) ? (list as RefEntity[]) : []);

const toIds = (value: unknown): string[] => (Array.isArray(value) ? value.map((entry) => String(entry)) : []);

export const matchesParents = (option: CategoryOption, parents: ParentMatch[]): boolean =>
  parents.every(({ relations, id }) => {
    if (!id) return true;
    /* The relation lives under one of two keys depending on the record shape
       (categoryOneId OR catOneIds). */
    const raw = relations.map((key) => (option as unknown as Record<string, unknown>)[key]).find((value) => value !== undefined && value !== null) ?? [];
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr.some((entry: { _id?: string } | string) => ((entry as { _id?: string })?._id || entry) === id);
  });

/**
 * Keep the row's current selection visible even when the parent filter would exclude it, so an
 * existing value never disappears from its own dropdown (mirrors CreateProduct's ensureInOptions).
 * @param filtered the parent-filtered options
 * @param source the unfiltered master to recover the selected entry from
 * @param selectedId the row's current value for the field
 * @returns the filtered list, plus the selected entry when it was filtered out
 */
const withSelected = (filtered: RefEntity[], source: RefEntity[], selectedId: unknown): RefEntity[] => {
  const id = String(selectedId ?? '');
  if (!id || filtered.some((option) => String(option?._id ?? '') === id)) return filtered;
  const found = source.find((option) => String(option?._id ?? '') === id);
  return found ? [...filtered, found] : filtered;
};

/**
 * Category 1 options for a row. Scoped to the row's product type only when the tenant runs
 * categories-based-on-product-type; without a product type that setting yields no options.
 * @param source the Category 1 master
 * @param catBasedOnProductType the tenant setting
 * @param productTypeId the row's product type
 * @param selectedId the row's current Category 1
 * @returns the options that row may choose from
 */
export const cat1For = (source: RefEntity[], catBasedOnProductType: boolean, productTypeId?: string, selectedId?: unknown): RefEntity[] => {
  if (!catBasedOnProductType) return source;
  if (!productTypeId) return withSelected([], source, selectedId);
  const filtered = source.filter((option) => {
    const types = (option as unknown as Record<string, unknown>).productType;
    return Array.isArray(types) && types.some((entry) => String((entry as { _id?: string })?._id ?? entry) === productTypeId);
  });
  return withSelected(filtered, source, selectedId);
};

/**
 * Category 2 options for a row: those whose Category 1 relation contains the row's Category 1.
 * Unfiltered when the tenant does not run categories-based-on-product-type.
 * @param source the Category 2 master
 * @param catBasedOnProductType the tenant setting
 * @param catOneId the row's Category 1
 * @param selectedId the row's current Category 2
 * @returns the options that row may choose from
 */
export const cat2For = (source: RefEntity[], catBasedOnProductType: boolean, catOneId?: string, selectedId?: unknown): RefEntity[] => {
  if (!catBasedOnProductType) return source;
  if (!catOneId) return withSelected([], source, selectedId);
  const filtered = source.filter((option) => matchesParents(option as CategoryOption, [{ relations: ['categoryOneId', 'catOneIds'], id: catOneId }]));
  return withSelected(filtered, source, selectedId);
};

export const cat3For = (source: RefEntity[], catOneId?: string, catTwoId?: string): RefEntity[] => {
  if (!catOneId || !catTwoId) return [];
  return source.filter((option) =>
    matchesParents(option as CategoryOption, [
      { relations: ['categoryOneId', 'catOneIds'], id: catOneId },
      { relations: ['categoryTwoId', 'catTwoIds'], id: catTwoId },
    ]),
  );
};

export const cat4For = (source: RefEntity[], catOneId?: string, catTwoId?: string, catThreeId?: string): RefEntity[] => {
  if (!catOneId || !catTwoId || !catThreeId) return [];
  return source.filter((option) =>
    matchesParents(option as CategoryOption, [
      { relations: ['categoryOneId', 'catOneIds'], id: catOneId },
      { relations: ['categoryTwoId', 'catTwoIds'], id: catTwoId },
      { relations: ['categoryThreeId', 'catThreeIds'], id: catThreeId },
    ]),
  );
};

export const sizesFor = (sizeRangeMaster: unknown, rangeIds: string[]): RefEntity[] => {
  if (!rangeIds.length) return [];
  return toOptions(sizeRangeMaster)
    .filter((range) => rangeIds.includes(String(range._id)))
    .flatMap((range) => toOptions((range as unknown as { sizes?: unknown }).sizes));
};

/**
 * Factory options for the selected suppliers. A factory marked inactive in Partner Overview is
 * left out unless the product already holds it, so existing selections still show and save.
 * @param supplierFactories supplier entries, each carrying a `factories` array
 * @param supplierIds the selected supplier ids
 * @param keepFactoryIds factory ids the product already holds
 * @returns the flattened factory options
 */
export const factoriesFor = (supplierFactories: unknown, supplierIds: string[], keepFactoryIds: string[] = []): RefEntity[] => {
  if (!supplierIds.length) return [];
  /* Each supplierFactories entry is a SUPPLIER carrying a `factories` array, so the dropdown
     needs the flattened factories. */
  return toOptions(supplierFactories)
    .filter((supplier) => supplierIds.includes(String((supplier as unknown as { supplierId?: string }).supplierId)))
    .flatMap((supplier) => toOptions((supplier as unknown as { factories?: unknown }).factories))
    .filter((factory) => (factory as unknown as { isActive?: boolean }).isActive !== false || keepFactoryIds.includes(String(factory._id)))
    .map((factory) => ({ _id: String(factory._id ?? ''), name: (factory as unknown as { factoryName?: string }).factoryName ?? factory.name ?? '' }));
};

export const likenessCacheKey = (licensorId: unknown, licenseId: unknown): string => `${String(licensorId ?? '')}|${String(licenseId ?? '')}`;

export const CASCADE_FIELDS: string[] = ['subBrandId', 'licenseId', 'likenessId', 'catThreeId', 'catFourId', 'sizeRangeSizes', 'supplierFactories', 'productTypeProcesses', 'catOneId', 'catTwoId'];

export const additionalVariableScopeKey = (row: TableRow | undefined): string =>
  [row?.brandId, row?.supplierType ?? row?.supplierTypeId, row?.catOneId, row?.catTwoId].map((value) => String(value ?? '')).join('_');

/** A Variable 2 config as the master list returns it: options plus the four scope id lists. */
export interface ScopedAdditionalVariable extends AdditionalVariable {
  isActive?: boolean;
  brand?: unknown[];
  productType?: unknown[];
  category1?: unknown[];
  category2?: unknown[];
}

const scopeIncludes = (scope: unknown[] | undefined, id: unknown): boolean =>
  Boolean(id) && Array.isArray(scope) && scope.some((entry) => String(entry) === String(id));

/**
 * The Variable 2 config matching the row, resolved the way the server's by-product read does: an
 * active config whose brand, product type, Category 1 and Category 2 lists all hold the row's value.
 */
export const matchedAdditionalVariable = (row: TableRow | undefined, variables: ScopedAdditionalVariable[]): ScopedAdditionalVariable | undefined =>
  variables.find(
    (variable) =>
      variable.isActive !== false &&
      scopeIncludes(variable.brand, row?.brandId) &&
      scopeIncludes(variable.productType, row?.supplierType ?? row?.supplierTypeId) &&
      scopeIncludes(variable.category1, row?.catOneId) &&
      scopeIncludes(variable.category2, row?.catTwoId),
  );

const sameCategory = (stored: unknown, selected: unknown): boolean => String(stored ?? '') === String(selected ?? '');

/**
 * Product Type Processes for the row: the process's product type must be the row's, and each category
 * level must equal the row's (an empty level matches only an empty one, as the server's scoped read).
 */
export const productTypeProcessesFor = (row: TableRow, processes: RefEntity[]): RefEntity[] =>
  processes.filter((processEntry) => {
    const scope = processEntry as RefEntity &
      Record<'productTypeId' | 'categoryOneId' | 'categoryTwoId' | 'categoryThreeId' | 'categoryFourId', unknown>;
    return (
      sameCategory(scope.productTypeId, row.supplierType) &&
      sameCategory(scope.categoryOneId, row.catOneId) &&
      sameCategory(scope.categoryTwoId, row.catTwoId) &&
      sameCategory(scope.categoryThreeId, row.catThreeId) &&
      sameCategory(scope.categoryFourId, row.catFourId)
    );
  });

export const resolveRowOptions = (row: TableRow | undefined, field: string, sources: CascadeSources): RefEntity[] | undefined => {
  if (!row) return undefined;
  switch (field) {
    case 'subBrandId':
      return sources.subBrandsByBrand[String(row.brandId ?? '')] ?? [];
    case 'licenseId':
      return sources.licensesByLicensor[String(row.licensorId ?? '')] ?? [];
    case 'likenessId':
      return sources.likenessByLicense[likenessCacheKey(row.licensorId, row.licenseId)] ?? [];
    case 'catOneId':
      return cat1For(sources.cat1Source, sources.catBasedOnProductType, row.supplierType as string | undefined, row.catOneId);
    case 'catTwoId':
      return cat2For(sources.cat2Source, sources.catBasedOnProductType, row.catOneId as string | undefined, row.catTwoId);
    case 'catThreeId':
      return cat3For(sources.cat3Source, row.catOneId as string | undefined, row.catTwoId as string | undefined);
    case 'catFourId':
      return cat4For(sources.cat4Source, row.catOneId as string | undefined, row.catTwoId as string | undefined, row.catThreeId as string | undefined);
    case 'sizeRangeSizes':
      return sizesFor(sources.sizeRangeMaster, toIds(row.sizeRangeId));
    case 'supplierFactories':
      return factoriesFor(sources.supplierFactories, toIds(row.suppliers), toIds(row.supplierFactories));
    case 'productTypeProcesses':
      return row.supplierType ? productTypeProcessesFor(row, sources.productTypeProcesses) : [];
    case 'additionalVariables':
      return toOptions(matchedAdditionalVariable(row, sources.additionalVariables)?.options);
    case 'seasonId':
      return withSelected(
        sources.seasonSource.filter((season) => (season as RefEntity & { isActive?: boolean }).isActive !== false),
        sources.seasonSource,
        row.seasonId,
      );
    default:
      return undefined;
  }
};

export interface AdditionalVariablesSelection {
  variableId: string;
  optionId: string[];
}

export const additionalVariableOptionIds = (selection: unknown): string[] => {
  const optionId = (selection as { optionId?: unknown } | null | undefined)?.optionId;
  return (Array.isArray(optionId) ? optionId : [optionId]).filter(Boolean).map(String);
};

export const toAdditionalVariablesSelection = (optionIds: string[], variable: AdditionalVariable | undefined): AdditionalVariablesSelection | null => {
  const allowed = new Set((variable?.options ?? []).map((option) => String(option._id)));
  const kept = optionIds.filter((optionId) => allowed.has(optionId));
  return variable && kept.length ? { variableId: String(variable._id), optionId: kept } : null;
};

/**
 * The factory selection after the Suppliers change, kept the way the product page keeps it: a
 * factory stays only while one of the selected suppliers still offers it, or while the product
 * already consumes it downstream; and a supplier set offering exactly one factory has it picked.
 * @param supplierFactories the supplier → factories master
 * @param supplierIds the suppliers now selected
 * @param selected the factories selected before the change
 * @param inUse factory ids the product already consumes (`orderFactoryId`)
 * @returns the factory ids to keep selected
 */
export const reconcileSupplierFactories = (
  supplierFactories: unknown,
  supplierIds: string[],
  selected: string[],
  inUse: ReadonlySet<string>,
): string[] => {
  const available = factoriesFor(supplierFactories, supplierIds).map((factory) => String(factory._id));
  const kept = selected.filter((factoryId) => available.includes(factoryId) || inUse.has(factoryId));
  const [onlyFactory] = available;
  return available.length === 1 && onlyFactory && !kept.includes(onlyFactory) ? [...kept, onlyFactory] : kept;
};

/**
 * The distinct countries of the selected factories — the `factoryCountry` the product page saves
 * alongside the factories, read by freight, duty and the critical path.
 * @param supplierFactories the supplier → factories master
 * @param factoryIds the selected factory ids
 * @returns each country once, in factory order
 */
export const factoryCountriesFor = (supplierFactories: unknown, factoryIds: string[]): string[] => {
  const countries = toOptions(supplierFactories)
    .flatMap((supplier) => toOptions((supplier as unknown as { factories?: unknown }).factories))
    .filter((factory) => factoryIds.includes(String(factory._id)))
    .map((factory) => (factory as unknown as { country?: string }).country)
    .filter((country): country is string => Boolean(country));
  return [...new Set(countries)];
};
